import { baseApi } from './baseApi';
import { RootState } from '../store';

export type ExportEntity =
  | 'instructors'
  | 'students'
  | 'staff'
  | 'enrollments'
  | 'courses'
  | 'attendance-records'
  | 'attendance-sessions'
  | 'certificates'
  | 'tickets'
  | 'payments'
  | 'results'
  | 'grades'
  | 'tasks'
  | 'task-submissions'
  | 'audit-logs'
  | 'programmes'
  | 'announcements'
  | 'lessons'
  | 'badges'
  | 'departments'
  | 'academic-sessions'
  | 'course-revisions'
  | 'attendance-policies'
  | 'chat-rooms'
  | 'chat-messages'
  | 'financial-reports'
  | 'enrollment-reports';

export type ExportFormat = 'excel' | 'csv';

/** Same optional filter fields as the entity list endpoint (exclude page/limit). */
export type ExportParams = Record<string, string | number | boolean | undefined | null>;

export interface ExportRequest {
  entity: ExportEntity;
  format: ExportFormat;
  params?: ExportParams;
}

/** @deprecated Use ExportParams */
export type ExportFilters = ExportParams;

function compactExportParams(params?: ExportParams): Record<string, string | number | boolean> {
  if (!params) return {};
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'page' || key === 'limit' || key === 'entity' || key === 'format') continue;
    out[key] = value;
  }
  return out;
}

export const exportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    exportData: builder.mutation<Blob, ExportRequest>({
      queryFn: async (body, _queryApi) => {
        const state = _queryApi.getState() as RootState;
        const token = state.auth.token;
        const baseUrl = import.meta.env.VITE_API_BASE_URL || "https://api-staging.fairchildinstitute.com/api/v1/institution";

        try {
          const response = await fetch(`${baseUrl}/staff/export`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { authorization: `Bearer ${token}` } : {}),
            },
            // Flat body: entity + format + optional list filter fields (search, status, …)
            body: JSON.stringify({
              entity: body.entity,
              format: body.format,
              ...compactExportParams(body.params),
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
            return {
              error: {
                status: response.status,
                data: errorData,
              },
            };
          }

          const blob = await response.blob();
          
          // Return the blob directly - RTK Query will handle it but we prevent caching
          return { data: blob };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          };
        }
      },
    }),
  }),
});

export const { useExportDataMutation } = exportApi;
